import { XcoreError, errorRecord } from "./errors";
import { AnyError } from "./errors/XcoreError";

/**
 * @deprecated Legacy error response type
 *  */
export interface ErrorResponse {
  title: string;
  status: number;
  detail?: string;
}

export interface BaseApiRequest<T> {
  url: string;

  then<TResult1 = T, TResult2 = never>(
    onfulfilled?: ((value: T, url: string) => TResult1 | PromiseLike<TResult1>) | undefined | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | undefined | null
  ): BaseApiRequest<TResult1 | TResult2>;

  catch<TResult = never>(onrejected?: ((reason: any) => TResult | PromiseLike<TResult>) | undefined | null): BaseApiRequest<T | TResult>;

  catchXcoreError<TResult = never, TError extends AnyError["name"] = AnyError["name"]>(
    name: TError,
    onrejected: OnRejected<TResult, AnyError & { name: TError }>
  ): BaseApiRequest<T | TResult>;

  catchXcoreError<TResult = never>(onrejected: OnRejected<TResult>): BaseApiRequest<T | TResult>;

  // todo resolve this in useXcoreQuery so that it returns error as it would return from swr
  toPromise: () => Promise<T | ErrorResponse>;
}

type OnRejected<T, TError extends AnyError = AnyError> = (reason: TError) => T | PromiseLike<T>;

/**
 * Structure which copies the promise interface, but also retains the requests url
 * @template {Promise<T>} T
 * @param {string} url
 *
 * @returns {BaseApiRequest<T>}
 * @throws {XcoreError}
 */
export const baseApiRequestHandler = <T>(url: string, p: Promise<T>): BaseApiRequest<T> => ({
  url,
  then: (onFulfilled, onRejected) => baseApiRequestHandler(url, p.then(onFulfilled && ((v) => onFulfilled(v, url)), onRejected)),
  catch: (onRejected) => baseApiRequestHandler(url, p.catch(onRejected)),
  catchXcoreError: <TResult = never>(
    name: XcoreError["name"] | OnRejected<TResult>,
    onRejected?: OnRejected<TResult>
  ): BaseApiRequest<T | TResult> => {
    const ErrorClass = typeof name === "string" ? errorRecord[name] : XcoreError;
    const handler = typeof name === "string" ? onRejected! : name;

    return baseApiRequestHandler(
      url,
      p.catch((e) => {
        if (e instanceof ErrorClass) return handler(e as Exclude<typeof e, XcoreError>);
        throw e;
      })
    );
  },

  toPromise: async () => await p,
});

/**
 * No-operation structure which copies the promise interface, also retains the request url and ignores any then transform
 * @template {Promise<T>} T
 * @param {string} url
 *
 * @returns {NoopPromise<T>}
 */
export const noopPromise = <T>(url: string, p: Promise<T>): BaseApiRequest<T> => ({
  ...baseApiRequestHandler(url, p),
  then: (_, onRejected) => noopPromise(url, p.then(undefined, onRejected)),
});
